import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  CartItem, 
  ShopifyProduct,
  createShopifyCart, 
  addLineToShopifyCart, 
  updateShopifyCartLine, 
  removeLineFromShopifyCart,
  storefrontApiRequest,
  CART_QUERY
} from '@/lib/shopify';

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  isOpen: boolean;
  
  // Actions
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<boolean>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
  openCart: () => void;
  closeCart: () => void;
  
  // Computed
  itemsCount: () => number;
  totalAmount: () => number;
}

export const useShopifyCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      isOpen: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        
        set({ isLoading: true });
        try {
          if (!cartId) {
            // Create new cart
            const result = await createShopifyCart({ ...item, lineId: null });
            if (!result) return false;
            set({
              cartId: result.cartId,
              checkoutUrl: result.checkoutUrl,
              items: [{ ...item, lineId: result.lineId }],
              isOpen: true
            });
            return true;
          } else if (existingItem) {
            // Update existing item quantity
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) {
              console.error('Cannot update quantity for item without lineId:', existingItem);
              return false;
            }
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
            if (result.success) {
              const currentItems = get().items;
              set({ 
                items: currentItems.map(i => 
                  i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i
                ),
                isOpen: true
              });
              return true;
            }
            if (result.cartNotFound) {
              clearCart();
            }
            return false;
          } else {
            // Add new item to existing cart
            const result = await addLineToShopifyCart(cartId, { ...item, lineId: null });
            if (result.success) {
              const currentItems = get().items;
              set({ 
                items: [...currentItems, { ...item, lineId: result.lineId ?? null }],
                isOpen: true
              });
              return true;
            }
            if (result.cartNotFound) {
              clearCart();
            }
            return false;
          }
        } catch (error) {
          console.error('Failed to add item:', error);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },


      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }
        
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            const currentItems = get().items;
            set({ items: currentItems.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to update quantity:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const currentItems = get().items;
            const newItems = currentItems.filter(i => i.variantId !== variantId);
            if (newItems.length === 0) {
              clearCart();
            } else {
              set({ items: newItems });
            }
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),
      
      getCheckoutUrl: () => get().checkoutUrl,
      
      openCart: () => set({ isOpen: true }),
      
      closeCart: () => set({ isOpen: false }),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return; // API error - preserve local cart
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to sync cart with Shopify:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      itemsCount: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      
      totalAmount: () => get().items.reduce((sum, item) => 
        sum + (parseFloat(item.price.amount) * item.quantity), 0
      ),
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        items: state.items, 
        cartId: state.cartId, 
        checkoutUrl: state.checkoutUrl 
      }),
    }
  )
);
